import { useState } from 'react';
import { useI18n } from '../i18n/I18nContext';

type MagicWord = {
  word: string;
  score: number;
  length: number;
};

type MagicResult = {
  words: MagicWord[];
  explanation: string;
  criteria: string;
};

async function performMagicSearch(query: string): Promise<MagicResult> {
  const response = await fetch('/api/magic-search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Search failed' }));
    throw new Error(error.error || 'Search failed');
  }

  return response.json();
}

export default function MagicSearchPage() {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<MagicResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (searchQuery?: string) => {
    const q = searchQuery || query;
    if (!q.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const data = await performMagicSearch(q);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
    handleSearch(example);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-magic-subtle via-surface to-page py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold font-heading text-primary mb-3 flex items-center justify-center gap-3">
            <span className="text-magic">✨</span>
            {t('magicTitle')}
          </h1>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            {t('magicSubtitle')}
          </p>
        </div>

        {/* Search Input */}
        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-magic/30 shadow-lg p-6 mb-8">
          <div className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('magicInputPlaceholder')}
              className="flex-1 border-2 border-magic/40 bg-input-bg rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-magic transition-colors text-primary"
            />
            <button
              onClick={() => handleSearch()}
              disabled={!query.trim() || loading}
              className="bg-magic hover:bg-magic-hover text-white font-bold py-3 px-8 rounded-btn disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
            >
              {loading ? t('magicSearching') : t('magicSearchButton')}
            </button>
          </div>

          {/* Examples */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-secondary mb-3">{t('magicExamplesTitle')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleExampleClick(t('magicExample1'))}
                className="px-4 py-2 text-sm bg-magic-subtle hover:bg-magic-subtle/80 text-magic-text rounded-lg transition-colors"
              >
                {t('magicExample1')}
              </button>
              <button
                onClick={() => handleExampleClick(t('magicExample2'))}
                className="px-4 py-2 text-sm bg-magic-subtle hover:bg-magic-subtle/80 text-magic-text rounded-lg transition-colors"
              >
                {t('magicExample2')}
              </button>
              <button
                onClick={() => handleExampleClick(t('magicExample3'))}
                className="px-4 py-2 text-sm bg-magic-subtle hover:bg-magic-subtle/80 text-magic-text rounded-lg transition-colors"
              >
                {t('magicExample3')}
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-magic border-t-transparent mb-4"></div>
            <p className="text-secondary text-lg">{t('magicSearching')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-error-subtle border border-error-border rounded-card p-6 mb-8">
            <h3 className="font-semibold text-error-text mb-2">{t('magicError')}</h3>
            <p className="text-error">{error}</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="space-y-6">
            {/* Criteria & Explanation */}
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-6">
              <div className="flex items-start gap-4">
                <div className="bg-magic-subtle p-3 rounded-lg">
                  <span className="text-2xl">🔮</span>
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">{result.criteria}</h3>
                  <p className="text-muted">{result.explanation}</p>
                </div>
              </div>
            </div>

            {/* Words Table */}
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6">
              <h2 className="text-xl font-semibold text-primary mb-4">
                {t('magicResultsTitle')} ({result.words.length})
              </h2>

              {result.words.length === 0 ? (
                <p className="text-center py-8 text-muted">{t('magicNoResults')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-subtle">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">#</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">Word</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Length</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-secondary">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.words.map((word, index) => (
                        <tr
                          key={`${word.word}-${index}`}
                          className="border-b border-subtle hover:bg-magic-subtle/40 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-magic-subtle text-magic-text font-semibold text-sm">
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-lg text-primary">
                              {word.word}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-secondary">
                            {word.length}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-block font-bold px-3 py-1 rounded-lg bg-magic text-white">
                              {word.score}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Why Magic Section */}
        {!result && !loading && (
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-6">
            <h2 className="font-semibold text-primary mb-2">{t('magicWhyTitle')}</h2>
            <p className="text-secondary">{t('magicWhyText')}</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-magic-text">
              <span>✨</span>
              <span>{t('magicPoweredBy')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
